// =============================================================================
// features/crm/components/TriageQueue.tsx — Visão "Triagem" do CRM (F55-S07).
//
// Lista os leads sem cidade e sem responsável — invisíveis na fila de toda
// cidade — para quem tem `leads:triage`, com a ação "Destinar" por linha.
// O gate de permissão fica no chamador (CrmListPage): sem a permissão este
// componente nem monta e o GET nunca sai.
//
// DS: ResponsiveTable (tabela densa no desktop, cards no mobile, skeleton),
// Input/Button/Badge primitivos, faixa de contexto em --surface-2.
// LGPD: o servidor já devolve o telefone mascarado (`phone_masked`) e a fila
// não traz e-mail nem documento — a tela só exibe o que recebe.
// =============================================================================

import * as React from 'react';
import { Link } from 'react-router-dom';

import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import type { ResponsiveTableColumn } from '../../../components/ui/ResponsiveTable';
import { ResponsiveTable } from '../../../components/ui/ResponsiveTable';
import { SOURCE_LABEL, formatDate } from '../../../hooks/crm/types';
import { cn } from '../../../lib/cn';
import type { TriageLeadItem, TriageListParams } from '../triage';
import { useTriageLeads } from '../triage';

import { TriageLeadDialog } from './TriageLeadDialog';

const PAGE_SIZE = 20;

function buildColumns(
  onDestine: (lead: TriageLeadItem) => void,
): ResponsiveTableColumn<TriageLeadItem>[] {
  return [
    {
      key: 'lead',
      header: 'Lead',
      primary: true,
      widthClassName: 'w-[260px]',
      cell: (lead) => (
        <Link
          to={`/crm/${lead.id}`}
          className="flex flex-col min-w-0 hover:text-azul transition-colors"
          title={`Ver detalhes de ${lead.name}`}
        >
          <span className="font-sans font-semibold text-sm text-ink truncate">{lead.name}</span>
          <span className="font-sans text-xs text-ink-4">Sem cidade · Sem responsável</span>
        </Link>
      ),
    },
    {
      key: 'phone',
      header: 'Telefone',
      widthClassName: 'w-[150px]',
      // LGPD: mascarado pelo servidor — o número inteiro nunca chega aqui.
      cell: (lead) => (
        <span className="font-mono text-ink-2" style={{ fontSize: '0.8125rem' }}>
          {lead.phone_masked}
        </span>
      ),
    },
    {
      key: 'source',
      header: 'Canal',
      hideBelow: 'md',
      widthClassName: 'w-[120px]',
      cell: (lead) => <Badge variant="neutral">{SOURCE_LABEL[lead.source] ?? lead.source}</Badge>,
    },
    {
      key: 'createdAt',
      header: 'Chegou em',
      hideBelow: 'lg',
      widthClassName: 'w-[120px]',
      cell: (lead) => (
        <span className="font-sans text-xs text-ink-4">{formatDate(lead.created_at)}</span>
      ),
    },
    {
      key: 'action',
      header: '',
      align: 'right',
      widthClassName: 'w-[130px]',
      cell: (lead) => (
        <Button size="sm" variant="outline" onClick={() => onDestine(lead)}>
          Destinar
        </Button>
      ),
    },
  ];
}

function TriageEmptyState({ filtered }: { filtered: boolean }): React.JSX.Element {
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-14 text-center">
      <p
        className="font-display font-bold text-ink"
        style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.025em' }}
      >
        {filtered ? 'Nenhum lead encontrado na triagem' : 'Fila de triagem vazia'}
      </p>
      <p className="font-sans text-sm text-ink-3 max-w-sm">
        {filtered
          ? 'Nenhum lead sem cidade e sem responsável corresponde à busca.'
          : 'Todo lead já tem cidade ou responsável. Novos contatos sem destino aparecem aqui.'}
      </p>
    </div>
  );
}

export function TriageQueue(): React.JSX.Element {
  const [params, setParams] = React.useState<TriageListParams>({ page: 1, limit: PAGE_SIZE });
  const [search, setSearch] = React.useState('');
  const [destining, setDestining] = React.useState<{ id: string; name: string } | null>(null);

  const searchTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  React.useEffect(
    () => () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    },
    [],
  );
  const handleSearch = (value: string): void => {
    setSearch(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      const trimmed = value.trim();
      setParams((p) =>
        trimmed ? { page: 1, limit: p.limit, search: trimmed } : { page: 1, limit: p.limit },
      );
    }, 300);
  };

  const { data, isLoading, isError, refetch } = useTriageLeads(params, { enabled: true });
  const leads = data?.data ?? [];
  const pagination = data?.pagination;

  const columns = React.useMemo(
    () => buildColumns((lead) => setDestining({ id: lead.id, name: lead.name })),
    [],
  );

  return (
    <div
      className="flex flex-col gap-4"
      style={{ animation: 'fade-up var(--dur-slow) var(--ease-out) both' }}
    >
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="max-w-xl">
          <div className="flex items-center gap-2">
            <h2
              className="font-display font-bold text-ink"
              style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.03em' }}
            >
              Triagem
            </h2>
            {pagination && (
              <Badge variant={pagination.total > 0 ? 'warning' : 'success'}>
                {pagination.total} aguardando
              </Badge>
            )}
          </div>
          <p className="font-sans text-sm text-ink-3 mt-1">
            Leads sem cidade e sem responsável não aparecem na fila de nenhuma cidade. Dê um destino
            a cada um.
          </p>
        </div>
        <div className="w-full sm:w-[280px]">
          <Input
            id="triage-search"
            placeholder="Buscar por nome ou telefone..."
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            wrapperClassName="w-full"
          />
        </div>
      </div>

      <div
        className="rounded-md border border-border overflow-hidden"
        style={{ background: 'var(--bg-elev-1)', boxShadow: 'var(--elev-2)' }}
      >
        {isError ? (
          <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
            <p className="font-sans text-sm text-danger">Erro ao carregar a fila de triagem.</p>
            <button
              type="button"
              className="font-sans text-xs font-semibold text-azul hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/20 rounded-xs"
              onClick={refetch}
            >
              Tentar novamente
            </button>
          </div>
        ) : (
          <ResponsiveTable
            columns={columns}
            data={leads}
            getRowKey={(lead) => lead.id}
            isLoading={isLoading}
            emptyState={<TriageEmptyState filtered={params.search !== undefined} />}
            aria-label="Fila de triagem de leads"
          />
        )}

        {pagination && pagination.totalPages > 1 && (
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-t border-border-subtle">
            <p className="font-sans text-xs text-ink-3">
              {(pagination.page - 1) * pagination.limit + 1}–
              {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total}{' '}
              leads
            </p>
            <div className="flex gap-2">
              {(['prev', 'next'] as const).map((dir) => {
                const disabled =
                  dir === 'prev' ? pagination.page <= 1 : pagination.page >= pagination.totalPages;
                return (
                  <button
                    key={dir}
                    type="button"
                    disabled={disabled}
                    onClick={() =>
                      setParams((p) => ({ ...p, page: p.page + (dir === 'prev' ? -1 : 1) }))
                    }
                    className={cn(
                      'px-3 py-1.5 rounded-sm font-sans text-xs font-medium',
                      'border border-border transition-all duration-fast',
                      'hover:bg-surface-hover hover:border-border-strong',
                      'disabled:opacity-40 disabled:cursor-not-allowed',
                      'focus-visible:ring-2 focus-visible:ring-azul/20',
                    )}
                    aria-label={dir === 'prev' ? 'Página anterior' : 'Próxima página'}
                  >
                    {dir === 'prev' ? '← Anterior' : 'Próxima →'}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <TriageLeadDialog lead={destining} onClose={() => setDestining(null)} />
    </div>
  );
}
